import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClientPhoto,
  ConfirmPhotoUpload,
  InitiatePhotoUpload,
  PhotoUploadTicket,
  UpdatePhoto,
} from '@salonomy/types';
import { api, unwrap, expectOk } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS-13 Feature 5 — BEFORE/AFTER PHOTOS (LRU-15). TanStack Query over the GENERATED, OpenAPI-typed `api`
 * client (lib/api.ts → createSalonomyClient — the WS2 non-negotiable; we never hand-roll fetch). The photo
 * paths (/v1/clients/:clientId/photos + /v1/photos/:id*) are now in the regenerated contract, so every
 * metadata call below is `unwrap(await api.GET/POST/PATCH(...))` exactly like data/consent.ts. Only the raw
 * presigned-PUT of the bytes (which the contract can't express) stays in lib/photos-api.ts. Mirrors
 * data/consent.ts (unwrap, body `as never`, onSuccess invalidate) and data/loyalty.ts (a per-client view
 * gated on the clientId).
 *
 * PRIVACY, NOT MONEY (the invalidation rationale): a client_photo is an identifiable PII image with a
 * SHORT-LIVED server-minted signed download URL. So unlike the loyalty/voucher/billing reads this is NOT
 * money-adjacent — but it gets staleTime:0 anyway for TWO privacy/integrity reasons: a cached signed URL
 * goes stale and renders a broken <img>, and a visibility/consent/delete write must be reflected
 * immediately (a photo that was just made client_visible or deleted must not linger). The clientPhotos key
 * is DELIBERATELY ABSENT from PERSISTED_PREFIXES too — client PII images are never mirrored to an offline
 * replica. EVERY write below invalidates THIS CLIENT'S photos (qk.clientPhotos) and nothing money-related.
 *
 * The FAIL-CLOSED visibility ladder + consent gate live in the server (route RBAC: client_visible is any
 * staff who can see the client; showcase needs consentMarketing recorded AND manager/owner). These hooks
 * POST intent and surface the server's 403/409/422/503 faithfully via ApiClientError (unwrap throws it) —
 * the UI never decides a visibility move is legal on its own (it only DISABLES controls to match, e.g.
 * showcase-until-consent).
 *
 * HONEST DISABLED STORAGE: the IMAGE BYTES live in an S3-compatible bucket that is INERT on staging (no
 * creds). useInitPhotoUpload's POST returns 503 ('photo storage not configured') in that posture; the hook
 * does not retry it (a 4xx/5xx config signal won't get better), and the gallery renders an honest "Photo
 * storage isn't configured yet" message instead of a broken uploader.
 */

// ── Reads ──────────────────────────────────────────────────────────────────────────

/**
 * One client's photo metadata (the staff/authed view). Each 'ready' row carries a freshly-minted
 * short-lived signed downloadUrl (minted server-side only after the withTenant read confirms the caller may
 * see this client's photos — NEVER another client's). The gallery groups these into before/after SETS by
 * pairId. Gated on a clientId so a closed detail pane doesn't fire. The list endpoint returns 200 with an
 * empty array when there's nothing yet (no 404 to map); a genuine error surfaces via isError. staleTime:0
 * for the signed-URL freshness + write-reflection reasons above.
 */
export function useClientPhotos(clientId: string | null) {
  return useQuery({
    queryKey: clientId ? qk.clientPhotos(clientId) : ['client-photos', 'none'],
    enabled: Boolean(clientId),
    staleTime: 0,
    queryFn: async (): Promise<ClientPhoto[]> =>
      unwrap(
        await api.GET('/v1/clients/{clientId}/photos', {
          params: { path: { clientId: clientId! } },
        }),
      ),
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────────

/**
 * Step 1 of the upload — POST init: validate content-type/size, mint a storage key + a 'pending' metadata
 * row (visibility defaults FAIL-CLOSED to salon_private), and return the presigned PUT ticket. Pass pairId
 * to attach to an existing SET (the 'after' of a 'before'); omit it to start a new set.
 *
 * This is the surface that signals DISABLED STORAGE: on staging (no bucket creds) the route returns 503,
 * which unwrap throws as ApiClientError(status:503). retry:false so we don't hammer a deliberately-inert
 * backend; the caller inspects err.status === 503 to show the honest "storage isn't configured" UX. We do
 * NOT invalidate here — a pending row carries no signed URL and isn't shown until confirm flips it to 'ready'.
 */
export function useInitPhotoUpload() {
  return useMutation({
    retry: false,
    mutationFn: async (input: {
      clientId: string;
      body: InitiatePhotoUpload;
    }): Promise<PhotoUploadTicket> =>
      unwrap(
        await api.POST('/v1/clients/{clientId}/photos', {
          params: { path: { clientId: input.clientId } },
          body: input.body as never,
        }),
      ),
  });
}

/**
 * Step 3 of the upload — POST confirm: flip the row 'pending' → 'ready' and record the real dims/byte size
 * (step 2, the presigned PUT of the bytes, happens in the component via uploadToPresignedUrl — it does NOT
 * touch our API). onSuccess invalidates THIS CLIENT'S photos so the gallery re-reads with the now-mintable
 * signed downloadUrl.
 */
export function useConfirmPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      clientId: string;
      photoId: string;
      body: ConfirmPhotoUpload;
    }): Promise<ClientPhoto> =>
      unwrap(
        await api.POST('/v1/photos/{id}/confirm', {
          params: { path: { id: input.photoId } },
          body: input.body as never,
        }),
      ),
    onSuccess: (_photo, input) => {
      void qc.invalidateQueries({ queryKey: qk.clientPhotos(input.clientId) });
    },
  });
}

/**
 * PATCH a photo's grouping/visibility — kind (before|after), re-pair (pairId), or a move along the
 * visibility ladder. The server enforces the ladder (client_visible = any staff who sees the client;
 * showcase = manager/owner AND consentMarketing recorded), 403/409-ing otherwise; we surface that
 * faithfully. onSuccess invalidates this client's photos (a visibility move must re-read immediately).
 */
export function useUpdatePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      clientId: string;
      photoId: string;
      body: UpdatePhoto;
    }): Promise<ClientPhoto> =>
      unwrap(
        await api.PATCH('/v1/photos/{id}', {
          params: { path: { id: input.photoId } },
          body: input.body as never,
        }),
      ),
    onSuccess: (_photo, input) => {
      void qc.invalidateQueries({ queryKey: qk.clientPhotos(input.clientId) });
    },
  });
}

/**
 * Record the DISTINCT "client consented to portfolio use" action — a privacy/likeness consent, NOT
 * clients.marketingConsent (messaging). The contract records it PER PHOTO (POST /v1/photos/:id/consent, no
 * body, stamps consentMarketing=true + consentCapturedAt/By and returns the updated row). The UI thinks in
 * SETS, so the caller hands us the set's photo ids and we stamp each — only AFTER this (and only a
 * manager/owner) can the set be promoted to 'showcase'. onSuccess invalidates this client's photos so the
 * showcase control un-gates.
 */
export function useCapturePhotoConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { clientId: string; photoIds: string[] }): Promise<ClientPhoto[]> => {
      const updated: ClientPhoto[] = [];
      for (const photoId of input.photoIds) {
        updated.push(
          unwrap(
            await api.POST('/v1/photos/{id}/consent', {
              params: { path: { id: photoId } },
            }),
          ),
        );
      }
      return updated;
    },
    onSuccess: (_photos, input) => {
      void qc.invalidateQueries({ queryKey: qk.clientPhotos(input.clientId) });
    },
  });
}

/**
 * DELETE a photo: the server drops the metadata row and enqueues the blob delete (storage + thumb keys) —
 * the image is genuinely erased, not just hidden (this is identifiable client PII). The UI gates this behind
 * a typed confirmation. onSuccess invalidates this client's photos.
 */
export function useDeletePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { clientId: string; photoId: string }): Promise<void> => {
      expectOk(
        await api.DELETE('/v1/photos/{id}', {
          params: { path: { id: input.photoId } },
        }),
      );
    },
    onSuccess: (_void, input) => {
      void qc.invalidateQueries({ queryKey: qk.clientPhotos(input.clientId) });
    },
  });
}
